/**
 * Create a recurring invoice plan per billed family so they land on the Billing
 * Plans table. Uses the same date/status helpers and `createOrganizationInvoicePlan`
 * write the production recurring-plan form uses; #5398's backend then charges on
 * the platform balance and holds the payout until Stripe onboarding completes.
 *
 * @handoff Single Tuition line item only. To reach full parity with the manual
 * plan builder (discounts, fees, taxes), route through the StepConfirmAndSave
 * payload instead of this direct call.
 */
import { createOrganizationInvoicePlan } from '../../api/firebase/invoices';
import { BillingIntervalEnum } from '../../enrollments/enums';
import { getInvoicePlanStatus, getUpdatedBillingDates } from '../../helpers/invoicePlans';
import { logError } from '../../rollbar';
import { hasBillingAmount, RosterRow } from '../AddFamilies/types';

const INTERVAL_MAP: Record<RosterRow['billingFrequency'], BillingIntervalEnum> = {
  weekly: BillingIntervalEnum.WEEKLY,
  biweekly: BillingIntervalEnum.BIWEEKLY,
  monthly: BillingIntervalEnum.MONTHLY,
};

const money = (v: string) => Number((v || '').replace(/[^0-9.]/g, '')) || 0;

interface ConfirmArgs {
  organizationId: string;
  rows: RosterRow[];
  // Real student docs keyed by id, used as the plan's payer when available.
  studentsById: Map<string, any>;
  createdBy: { id: string; displayName: string };
  timezone?: string;
}

export async function confirmAddToBilling({
  organizationId,
  rows,
  studentsById,
  createdBy,
  timezone = 'utc',
}: ConfirmArgs): Promise<number> {
  const billed = rows.filter(hasBillingAmount);
  let created = 0;

  for (const row of billed) {
    const student = studentsById.get(row.id);
    if (!student) continue; // only create plans for real students

    // Same shape the production recurring-plan form builds
    // (Components/Forms/Billing/StepInvoicePlanDetails.jsx): one date, passed as
    // BOTH dateStart and dateDue, then handed to getUpdatedBillingDates with
    // isEditing so the chosen due date is honored rather than re-derived. That
    // form has no separate recurring due-day either — the schedule repeats from
    // this date (monthly on its day-of-month, weekly/biweekly on its weekday).
    const planDate = row.startDate || Date.now();
    const details: any = {
      dateStart: planDate,
      dateDue: planDate,
      billingInterval: INTERVAL_MAP[row.billingFrequency],
      notificationDays: 3,
      billFor: row.billFor === 'prior' ? 'Prior' : 'Next',
      createdBy,
    };

    const dates = getUpdatedBillingDates({ invoicePlan: details, timezone, isEditing: true });
    const amount = money(row.billingAmount);
    const invoiceItems = [{ category: 'Tuition', description: 'Tuition', amount, quantity: 1, discounts: [] }];
    const status = getInvoicePlanStatus(dates.dateDue, (dates as any).dateEnd);

    const payload = {
      ...details,
      ...dates,
      isInvoice: false,
      status,
      total: amount,
      invoiceItems,
      invoiceItemList: invoiceItems,
      students: [student],
    };

    try {
      await createOrganizationInvoicePlan(organizationId, payload);
      created += 1;
    } catch (err) {
      logError('AddToBilling: failed to create plan', { row, err });
    }
  }

  return created;
}
